<template>
  <div>
    <v-alert v-if="!canManage" type="warning" outlined class="ma-2">
      Missing right: MANAGE_PHOENIX_BOTE
    </v-alert>

    <template v-else>
      <v-row class="ma-2 align-center">
        <v-text-field class="pa-2" v-model="search" append-icon="mdi-magnify"
                      :label="$t('general.misc.search')" clearable hide-details></v-text-field>
        <v-chip :color="!filterOnArchived ? 'dark-gray' : archivedFilter ? 'primary' : 'info'"
                text-color="white" class="mt-3 ml-2 mr-2"
                @contextmenu="$event.preventDefault(); archivedFilter = !archivedFilter"
                @click="filterOnArchived = !filterOnArchived">
          <v-avatar left>
            <v-icon>{{ archivedFilter ? 'mdi-archive-plus' : 'mdi-archive-minus' }}</v-icon>
          </v-avatar>
          <span v-if="archivedFilter">
            {{ $t('management.phoenix_bote.archived') }}
          </span>
          <span v-else>
            {{ $t('management.phoenix_bote.not_archived') }}
          </span>
        </v-chip>
        <v-spacer></v-spacer>
        <v-btn @click="groupSelectedPages" :disabled="selectedPageIds.length !== 2" color="info" class="mr-2 mt-2">
          <v-icon>mdi-link</v-icon>
          <span class="ml-1">Gruppieren ({{ selectedPageIds.length }}/2)</span>
        </v-btn>
        <v-btn @click="openReorderDialog" color="secondary" class="mr-2 mt-2">
          <v-icon>mdi-format-list-numbered</v-icon>
          <span class="ml-1">{{ $t('management.phoenix_bote.reorder') }}</span>
        </v-btn>
        <v-btn @click="isOpenCreateDialog = true" color="primary" class="mr-1 mt-2">
          <v-icon>mdi-plus-circle</v-icon>
          <span class="ml-1">{{ $t('general.actions.create') }}</span>
        </v-btn>
      </v-row>

      <v-data-table :headers="headers" :items="pages" item-key="id"
                    :search="search" :loading="isLoading"
                    :page.sync="page" :items-per-page="itemsPerPage" hide-default-footer
                    @page-count="pageCount = $event" :custom-filter="Utilities().searchNormalized">
        <template v-slot:item.title="{ item }">
          <v-row class="pa-2 align-center" dense v-if="item.edit">
            <v-checkbox :input-value="selectedPageIds.includes(item.id)" @change="togglePageSelection(item.id)" :disabled="item.groupId" dense hide-details class="mr-2"></v-checkbox>
            <v-text-field v-model="item.title" hide-details class="mr-2"></v-text-field>
            <v-tooltip top>
              <template v-slot:activator="{ on }">
                <v-btn @click="updatePage(item)" icon class="mb-1" color="primary" v-on="on">
                  <v-icon>mdi-content-save</v-icon>
                </v-btn>
              </template>
              <span>{{ $t('general.actions.save') }}</span>
            </v-tooltip>
          </v-row>
          <v-row class="pa-2 align-center" dense v-else>
            <v-checkbox :input-value="selectedPageIds.includes(item.id)" @change="togglePageSelection(item.id)" :disabled="item.groupId" dense hide-details class="mr-2"></v-checkbox>
            <span><b>#{{ item.pageNumber }}</b> - {{ item.title }}</span>
            <v-chip v-if="item.groupId" small color="warning" text-color="white" class="ml-2">
              <v-icon small>mdi-link</v-icon>
              <span class="ml-1">Gruppiert ({{ item.groupId.substring(0, 8) }})</span>
            </v-chip>
            <v-tooltip top>
              <template v-slot:activator="{ on }">
                <v-btn @click="item.edit = !item.edit" icon class="mb-1" color="primary" v-on="on">
                  <v-icon>mdi-pencil-circle</v-icon>
                </v-btn>
              </template>
              <span>{{ $t('general.actions.edit') }}</span>
            </v-tooltip>
          </v-row>
        </template>
        <template v-slot:item.groupId="{ item }">
          <span v-if="item.groupId" class="font-monospace text-caption">{{ item.groupId.substring(0, 12) }}</span>
          <span v-else class="text-disabled">-</span>
        </template>
        <template v-slot:item.isArchived="{ item }">
          <v-chip small :color="item.isArchived ? 'primary' : 'info'" text-color="white" class="mr-1 mb-1">
            <v-avatar left>
              <v-icon small>{{ item.isArchived ? 'mdi-archive-plus' : 'mdi-archive-minus' }}</v-icon>
            </v-avatar>
            <span v-if="item.isArchived">
              {{ $t('management.phoenix_bote.archived') }}
            </span>
            <span v-else>
              {{ $t('management.phoenix_bote.not_archived') }}
            </span>
          </v-chip>
        </template>
        <template v-slot:item.actions="{ item }">
          <v-tooltip top>
            <template v-slot:activator="{ on }">
              <v-btn @click="openPreview(item.content)" icon color="primary" v-on="on">
                <v-icon>mdi-image</v-icon>
              </v-btn>
            </template>
            <span>{{ $t('management.phoenix_bote.preview') }}</span>
          </v-tooltip>

          <v-tooltip top v-if="!item.isArchived">
            <template v-slot:activator="{ on }">
              <v-btn @click="updateArchiveState(item, true)" icon color="primary" v-on="on">
                <v-icon>mdi-archive-plus</v-icon>
              </v-btn>
            </template>
            <span>{{ $t('management.phoenix_bote.archive') }}</span>
          </v-tooltip>
          <v-tooltip top v-if="item.isArchived">
            <template v-slot:activator="{ on }">
              <v-btn @click="updateArchiveState(item, false)" icon color="primary" v-on="on">
                <v-icon>mdi-archive-minus</v-icon>
              </v-btn>
            </template>
            <span>{{ $t('management.phoenix_bote.activate') }}</span>
          </v-tooltip>

          <v-tooltip top>
            <template v-slot:activator="{ on }">
              <v-btn @click="deletePage(item)" icon color="primary" v-on="on">
                <v-icon>mdi-delete</v-icon>
              </v-btn>
            </template>
            <span>{{ $t('management.phoenix_bote.remove') }}</span>
          </v-tooltip>

          <v-tooltip top v-if="item.groupId">
            <template v-slot:activator="{ on }">
              <v-btn @click="ungroupPage(item)" icon color="warning" v-on="on">
                <v-icon>mdi-link-off</v-icon>
              </v-btn>
            </template>
            <span>Entgruppieren</span>
          </v-tooltip>
        </template>
      </v-data-table>
      <div class="text-center pt-2">
        <v-pagination v-if="pages.length > 0" v-model="page" :length="pageCount"></v-pagination>
      </div>

      <v-dialog v-model="isOpenCreateDialog" max-width="450px">
        <v-card>
          <v-toolbar flat dense color="orange darken-3">
            <v-icon class="mr-1">mdi-newspaper-plus</v-icon>
            {{ $t('management.phoenix_bote.create') }}
          </v-toolbar>
          <v-card-text class="pa-3">
            <v-text-field :label="$t('management.phoenix_bote.table.title')"
                          v-model="dialogModel.title" class="mr-2"/>
            <v-alert dense text type="info" class="mt-2 mb-0">
              {{ $t('management.phoenix_bote.latest_page_hint', { page: nextNotArchivedPageNumber }) }}
            </v-alert>
            <v-file-input :label="$t('management.phoenix_bote.table.image')"
                          v-model="dialogModel.image" accept="image/png, image/jpeg, image/webp"
                          class="mr-2"></v-file-input>
          </v-card-text>
          <v-card-actions class="pa-3 justify-end">
            <v-btn class="mr-2" @click="isOpenCreateDialog = false">
              {{ $t('general.actions.cancel') }}
            </v-btn>
            <v-btn @click="uploadPage()"
                   :disabled="dialogModel.image === null || dialogModel.title === ''"
                   color="primary">
              {{ $t('management.phoenix_bote.create') }}
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <v-dialog v-model="isOpenReorderDialog" max-width="700px">
        <v-card>
          <v-toolbar flat dense color="orange darken-3">
            <v-icon class="mr-1">mdi-format-list-numbered</v-icon>
            {{ $t('management.phoenix_bote.reorder') }}
            <v-spacer></v-spacer>
            <v-btn icon @click="isOpenReorderDialog = false">
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </v-toolbar>
          <v-card-text class="pa-3">
            <p class="mb-3 grey--text text--lighten-1">{{ $t('management.phoenix_bote.reorder_hint') }}</p>
            <div class="reorder-list-wrap">
              <draggable
                v-model="reorderItems"
                handle=".drag-handle"
                :scroll="true"
                :scroll-sensitivity="120"
                :scroll-speed="14"
                :bubble-scroll="true"
              >
                <div
                  v-for="(item, idx) in reorderItems"
                  :key="item.id"
                  class="d-flex align-center pa-2 mb-2 reorder-entry"
                >
                  <v-icon class="drag-handle mr-2">mdi-drag</v-icon>
                  <span class="mr-3 grey--text">#{{ idx + 1 }}</span>
                  <span>{{ item.title }}</span>
                </div>
              </draggable>
            </div>
          </v-card-text>
          <v-card-actions class="pa-3 justify-end">
            <v-btn class="mr-2" @click="isOpenReorderDialog = false">
              {{ $t('general.actions.cancel') }}
            </v-btn>
            <v-btn color="primary" :loading="isSavingReorder" @click="saveReorder">
              {{ $t('general.actions.save') }}
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <v-dialog v-model="isOpenPreviewDialog" max-width="900px">
        <v-card class="mx-auto">
          <v-card-text class="pa-0">
            <div class="preview-shell">
              <div class="preview-controls">
                <v-btn icon class="preview-control-btn mr-2" @click="togglePreviewZoom">
                  <v-icon>{{ previewZoom > 1 ? 'mdi-magnify-minus-outline' : 'mdi-magnify-plus-outline' }}</v-icon>
                </v-btn>
                <v-btn icon class="preview-control-btn" @click="isOpenPreviewDialog = false">
                  <v-icon>mdi-close</v-icon>
                </v-btn>
              </div>
              <div class="preview-image-wrap">
                <img
                  v-if="preview"
                  :src="preview"
                  class="preview-image"
                  :style="{ transform: `scale(${previewZoom})` }"
                  alt="Phoenix-Bote preview"
                >
              </div>
            </div>
          </v-card-text>
        </v-card>
      </v-dialog>
    </template>
  </div>
</template>

<script>
import draggable from "vuedraggable";
import DataService from "@/services/data.service";
import MessageService from "@/services/message.service";
import Utilities from "@/util/Utilities";
import {fileToDataUrl, optimizeImageForFlipbook} from "@/util/FlipbookImage";

export default {
  name: "PhoenixBoteManagement",
  components: {draggable},
  props: {
    embedded: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      search: "",
      isLoading: true,
      page: 1,
      pageCount: 0,
      itemsPerPage: 100,
      filterOnArchived: false,
      archivedFilter: false,
      isOpenCreateDialog: false,
      isOpenReorderDialog: false,
      isSavingReorder: false,
      isOpenPreviewDialog: false,
      preview: null,
      previewZoom: 1,
      reorderItems: [],
      selectedPageIds: [],
      dialogModel: {
        title: "",
        image: null
      },
      pages: [],
      headers: [
        {text: this.$t('management.phoenix_bote.table.title'), value: "title"},
        {
          text: this.$t('management.phoenix_bote.archived'),
          value: "isArchived",
          filter: value => {
            return !this.filterOnArchived || value === this.archivedFilter;
          }
        },
        {text: "Gruppe", value: "groupId"},
        {text: this.$t('general.timestamps.created_at'), value: "createdAt"},
        {text: this.$t('general.timestamps.updated_at'), value: "updatedAt"},
        {text: this.$t('general.misc.actions'), value: "actions"}
      ]
    }
  },
  async mounted() {
    if (!this.canManage) {
      return;
    }
    await this.loadPages();
  },
  methods: {
    Utilities() {
      return Utilities;
    },
    normalizePages(pages) {
      return pages
          .map(page => ({
            ...page,
            edit: false,
            updatedAtRaw: page.updatedAt,
            createdAt: `${new Date(page["createdAt"]).toLocaleDateString()} ${new Date(page["createdAt"]).toLocaleTimeString()}`,
            updatedAt: `${new Date(page["updatedAt"]).toLocaleDateString()} ${new Date(page["updatedAt"]).toLocaleTimeString()}`,
          }))
          .sort((a, b) => {
            if (a.isArchived !== b.isArchived) {
              return a.isArchived ? 1 : -1;
            }
            if (!a.isArchived) {
              return a.pageNumber - b.pageNumber;
            }
            return new Date(b.updatedAtRaw).getTime() - new Date(a.updatedAtRaw).getTime();
          });
    },
    async loadPages() {
      this.isLoading = true;
      const pages = await DataService.getPhoenixBotePages(false);
      this.pages = this.normalizePages(pages);
      this.isLoading = false;
    },
    async optimizeUploadImage(file) {
      const dataUrl = await fileToDataUrl(file);
      return optimizeImageForFlipbook(dataUrl, {
        maxWidth: 1200,
        maxHeight: 1700,
        maxLength: 900000
      });
    },
    async uploadPage() {
      const optimizedImage = await this.optimizeUploadImage(this.dialogModel.image).catch(() => null);
      if (!optimizedImage) {
        MessageService.error("Could not upload PhoenixBote page!");
        return;
      }

      DataService.createPhoenixBotePage(this.dialogModel.title, optimizedImage).then(data => {
        if (data) {
          this.isOpenCreateDialog = false;
          this.dialogModel.title = "";
          this.dialogModel.image = null;
          MessageService.success("PhoenixBote page successfully uploaded.");
          this.loadPages();
        }
      });
    },
    updateArchiveState(page, isArchived) {
      DataService.updatePhoenixBotePage(page.id, {
        isArchived: isArchived
      }).then(success => {
        if (success) {
          MessageService.success("PhoenixBote page successfully updated.");
          this.loadPages();
        }
      });
    },
    updatePage(page) {
      if (!page.title) {
        return;
      }

      DataService.updatePhoenixBotePage(page.id, {
        title: page.title
      }).then(success => {
        if (success) {
          page.edit = false;
          MessageService.success("PhoenixBote page successfully updated.");
          this.loadPages();
        }
      });
    },
    openPreview(content) {
      this.preview = content;
      this.previewZoom = 1;
      this.isOpenPreviewDialog = true;
    },
    togglePreviewZoom() {
      this.previewZoom = this.previewZoom > 1 ? 1 : 2;
    },
    openReorderDialog() {
      this.reorderItems = this.pages
          .filter(page => !page.isArchived)
          .sort((a, b) => a.pageNumber - b.pageNumber)
          .map(page => ({ id: page.id, title: page.title }));
      this.isOpenReorderDialog = true;
    },
    saveReorder() {
      const pageIds = this.reorderItems.map(item => item.id);
      this.isSavingReorder = true;
      DataService.reorderPhoenixBotePages(pageIds).then(success => {
        if (success) {
          MessageService.success("PhoenixBote pages successfully reordered.");
          this.isOpenReorderDialog = false;
          this.loadPages();
        }
      }).finally(() => {
        this.isSavingReorder = false;
      });
    },
    deletePage(page) {
      DataService.deletePhoenixBotePage(page.id).then(success => {
        if (success) {
          const index = this.pages.findIndex(x => x.id === page.id);
          if (index !== -1) {
            this.pages.splice(index, 1);
            MessageService.success("PhoenixBote page successfully deleted.");
          }
        }
      });
    },
    togglePageSelection(pageId) {
      const index = this.selectedPageIds.indexOf(pageId);
      if (index > -1) {
        this.selectedPageIds.splice(index, 1);
      } else {
        this.selectedPageIds.push(pageId);
      }
    },
    groupSelectedPages() {
      if (this.selectedPageIds.length !== 2) {
        MessageService.warn("Bitte wählen Sie genau 2 Seiten zum Gruppieren aus.");
        return;
      }
      const [pageId1, pageId2] = this.selectedPageIds;
      DataService.groupPhoenixBotePages(pageId1, pageId2).then(success => {
        if (success) {
          MessageService.success("Seiten erfolgreich gruppiert.");
          this.selectedPageIds = [];
          this.loadPages();
        }
      });
    },
    ungroupPage(page) {
      if (!page.groupId) {
        MessageService.warn("Diese Seite ist nicht gruppiert.");
        return;
      }
      DataService.ungroupPhoenixBotePage(page.id).then(success => {
        if (success) {
          MessageService.success("Seiten erfolgreich entgruppiert.");
          this.loadPages();
        }
      });
    }
  },
  computed: {
    currentUser() {
      return this.$store.getters["auth/user"];
    },
    canManage() {
      return this.currentUser && this.currentUser.rights?.includes("MANAGE_PHOENIX_BOTE");
    },
    nextNotArchivedPageNumber() {
      const activePages = this.pages.filter(page => !page.isArchived);
      if (activePages.length === 0) return 1;
      const latestPage = activePages.reduce((latest, page) => Math.max(latest, page.pageNumber || 0), 0);
      return latestPage + 1;
    }
  }
}
</script>

<style scoped>
.reorder-list-wrap {
  max-height: 55vh;
  overflow-y: auto;
  padding-right: 6px;
}

.preview-shell {
  position: relative;
}

.preview-image-wrap {
  max-height: 70vh;
  overflow: auto;
  display: flex;
  justify-content: center;
  align-items: flex-start;
  background: #121212;
  padding: 8px;
}

.preview-image {
  transform-origin: top center;
  max-width: 100%;
}

.preview-controls {
  position: absolute;
  top: 12px;
  right: 12px;
  z-index: 5;
  display: flex;
  align-items: center;
}

.preview-control-btn {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: rgba(18, 18, 18, 0.8);
  border: 1px solid rgba(255, 255, 255, 0.55);
}

.reorder-entry {
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 6px;
  background: rgba(255, 255, 255, 0.03);
}

.drag-handle {
  cursor: grab;
}
</style>




